import { useCallback, useEffect, useState } from "react";
import { chatbotApi, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Loader2, Mail, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";

// BC-018: who receives the "New lead" email for this chatbot.
const STATUS = {
  active: { label: "Active", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  pending: { label: "Waiting for confirmation", cls: "text-amber-700 bg-amber-50 border-amber-200" },
  unsubscribed: { label: "Unsubscribed", cls: "text-muted-foreground bg-muted border-border" },
};

export default function LeadRecipientsPanel({ chatbotId }) {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await chatbotApi.listLeadRecipients(chatbotId);
      setData(res.data);
    } catch (e) {
      setData({ error: formatApiError(e?.response?.data?.detail) });
    }
  }, [chatbotId]);

  useEffect(() => { load(); }, [load]);

  const run = async (key, fn, ok) => {
    if (busy) return;
    setBusy(key);
    try {
      await fn();
      if (ok) toast.success(ok);
      await load();
      return true;
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
      await load();
      return false;
    } finally {
      setBusy("");
    }
  };

  const add = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value) return;
    const done = await run("add", () => chatbotApi.addLeadRecipient(chatbotId, value),
      "Confirmation email sent. They start receiving leads once they confirm.");
    if (done) setEmail("");
  };

  if (!data) {
    return (
      <div className="border border-border p-6 text-sm text-muted-foreground flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading lead email settings
      </div>
    );
  }
  if (data.error) {
    return <div className="border border-border p-6 text-sm text-muted-foreground">{data.error}</div>;
  }

  const list = data.recipients || [];
  const full = list.length >= (data.max || 5);

  return (
    <div className="border border-border p-6 space-y-5" data-testid="lead-recipients-panel">
      <div>
        <h2 className="font-semibold">Lead email notifications</h2>
        <p className="text-sm text-muted-foreground">
          Who gets an email when this chatbot captures a new lead. Each person gets their own email, so recipients
          never see each other's addresses.
        </p>
      </div>

      {!data.email_configured && (
        <p className="text-sm border border-amber-200 bg-amber-50 text-amber-800 p-3">
          Email sending is not configured on this server, so no lead emails are sent yet.
        </p>
      )}

      <div className="flex items-center justify-between gap-4 border border-border p-4">
        <div className="min-w-0">
          <p className="font-medium text-sm">Workspace owner</p>
          <p className="text-sm text-muted-foreground truncate">{data.owner_email || "No owner email found"}</p>
        </div>
        <Switch
          checked={!!data.owner_enabled}
          disabled={busy === "owner"}
          onCheckedChange={(v) => run("owner", () => chatbotApi.setLeadOwnerEmail(chatbotId, v),
            v ? "The owner will receive lead emails" : "The owner will no longer receive lead emails")}
          data-testid="lead-owner-toggle"
        />
      </div>

      <div className="space-y-2">
        <p className="font-medium text-sm">Other recipients ({list.length}/{data.max || 5})</p>
        <p className="text-xs text-muted-foreground">
          Lead emails contain visitors' contact details, so every address must confirm through a link before it
          receives anything. Each lead email also has a link to stop them.
        </p>
        {list.length > 0 && (
          <ul className="divide-y divide-border border border-border">
            {list.map((r) => {
              const st = STATUS[r.status] || STATUS.pending;
              return (
                <li key={r.id} className="flex flex-wrap items-center gap-3 p-3">
                  <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="text-sm break-all flex-1 min-w-[160px]">{r.email}</span>
                  <span className={`text-xs border px-2 py-0.5 ${st.cls}`}>{st.label}</span>
                  {r.status === "pending" && (
                    <Button
                      type="button" variant="outline" size="sm" className="rounded-none"
                      disabled={!!busy}
                      onClick={() => run(`resend-${r.id}`, () => chatbotApi.resendLeadRecipient(chatbotId, r.id), "Confirmation email sent again")}
                    >
                      {busy === `resend-${r.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                      <span className="ml-1">Resend</span>
                    </Button>
                  )}
                  <Button
                    type="button" variant="outline" size="sm" className="rounded-none"
                    disabled={!!busy}
                    aria-label={`Remove ${r.email}`}
                    onClick={() => {
                      if (!window.confirm(`Stop sending lead emails to ${r.email}?`)) return;
                      run(`del-${r.id}`, () => chatbotApi.deleteLeadRecipient(chatbotId, r.id), "Recipient removed");
                    }}
                  >
                    {busy === `del-${r.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
        <form onSubmit={add} className="flex flex-col sm:flex-row gap-2">
          <Input
            type="email" value={email} maxLength={254}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={full ? "Maximum reached. Remove one to add another." : "name@example.com"}
            disabled={full || !!busy || !data.email_configured}
            className="rounded-none"
            data-testid="lead-recipient-input"
          />
          <Button type="submit" className="rounded-none" disabled={full || !!busy || !email.trim() || !data.email_configured}>
            {busy === "add" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add recipient"}
          </Button>
        </form>
      </div>
    </div>
  );
}
